import { useEffect, useState } from "react";
import { Mail, MapPin, Facebook, Send, CheckCircle2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import { toast } from "sonner";

export default function Contact() {
  const [site, setSite] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", message: "", company: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    api.get("/site").then((r) => setSite(r.data)).catch(() => {});
  }, []);

  const email = site?.contact_email || "quintebca@gmail.com";
  const region = site?.contact_region || "Trenton & the Quinte region, Ontario";
  const facebook = site?.facebook_url || "";

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.includes("@") || !form.message.trim()) {
      toast.error("Please add your name, a valid email and a message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", message: "", company: "" });
      toast.success("Message sent — we'll be in touch soon!");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please email us directly.";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <Layout>
      <Seo title="Contact Quinte BCA Pool League | Trenton, Ontario" description="Get in touch with the Quinte BCA Pool League about joining a division, sponsorship, tournaments or general questions in the Quinte region." path="/contact" />
      <PageHeader
        eyebrow="Get in touch"
        title="Contact Us"
        subtitle={site?.contact_intro || "Questions about joining a division, partnership, or anything else? We'd love to hear from you."}
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 grid gap-8 lg:grid-cols-5">
        {/* Form */}
        <div className="lg:col-span-3 bg-white rounded-lg border border-[#E5E7EB] shadow-sm p-6 sm:p-8">
          <h2 className="font-heading text-2xl font-bold mb-1">Send us a message</h2>
          <p className="text-sm text-[#555555] mb-6">We typically reply within a day or two.</p>

          {sent ? (
            <div className="flex flex-col items-center text-center gap-3 py-10" data-testid="contact-success">
              <CheckCircle2 className="h-12 w-12 text-[#1E63C4]" />
              <p className="font-heading text-xl font-bold">Thanks for reaching out!</p>
              <p className="text-[#555555]">Your message is on its way. We&apos;ll get back to you soon.</p>
              <button
                onClick={() => setSent(false)}
                data-testid="contact-send-another"
                className="mt-2 text-[#1E63C4] font-semibold hover:underline"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              {/* Honeypot — hidden from real users; bots that fill it are silently dropped */}
              <input
                type="text"
                name="company"
                value={form.company}
                onChange={update("company")}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                data-testid="contact-company-honeypot"
                className="absolute -left-[9999px] top-[-9999px] h-0 w-0 opacity-0"
              />
              <div>
                <label className="block text-sm font-semibold text-[#111111] mb-1.5">Your name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={update("name")}
                  data-testid="contact-name-input"
                  className="w-full rounded-md border border-[#D1D5DB] px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#1E63C4]/40 focus:border-[#1E63C4]"
                  placeholder="Jane Smith"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#111111] mb-1.5">Your email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  data-testid="contact-email-input"
                  className="w-full rounded-md border border-[#D1D5DB] px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#1E63C4]/40 focus:border-[#1E63C4]"
                  placeholder="you@example.com"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#111111] mb-1.5">Message</label>
                <textarea
                  value={form.message}
                  onChange={update("message")}
                  data-testid="contact-message-input"
                  rows={5}
                  className="w-full rounded-md border border-[#D1D5DB] px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#1E63C4]/40 focus:border-[#1E63C4] resize-y"
                  placeholder="How can we help?"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="inline-flex items-center gap-2 bg-[#1E63C4] text-white rounded-md px-6 py-3 font-semibold hover:bg-[#174e9c] disabled:opacity-60 transition-colors"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {sending ? "Sending…" : "Send message"}
              </button>
            </form>
          )}
        </div>

        {/* Direct contact options */}
        <div className="lg:col-span-2 space-y-4">
          <h2 className="font-heading text-2xl font-bold mb-2">Reach the League</h2>
          <a
            href={`mailto:${email}`}
            data-testid="contact-email-link"
            className="flex items-center gap-4 p-4 rounded-md border border-[#E5E7EB] bg-white hover:border-[#1E63C4] hover:bg-blue-50 transition-colors"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1E63C4] text-white">
              <Mail className="h-5 w-5" />
            </span>
            <div>
              <p className="font-semibold text-[#111111]">Email</p>
              <p className="text-[#1E63C4] text-sm break-all">{email}</p>
            </div>
          </a>

          {facebook && (
            <a
              href={facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-facebook-link"
              className="flex items-center gap-4 p-4 rounded-md border border-[#E5E7EB] bg-white hover:border-[#1E63C4] hover:bg-blue-50 transition-colors"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1877F2] text-white">
                <Facebook className="h-5 w-5" />
              </span>
              <div>
                <p className="font-semibold text-[#111111]">Facebook</p>
                <p className="text-[#1E63C4] text-sm">Follow us for news, photos &amp; updates</p>
              </div>
            </a>
          )}

          {region && (
            <div className="flex items-center gap-4 p-4 rounded-md border border-[#E5E7EB] bg-white" data-testid="contact-region">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#62A8E5]/20 text-[#1E63C4]">
                <MapPin className="h-5 w-5" />
              </span>
              <div>
                <p className="font-semibold text-[#111111]">Region</p>
                <p className="text-[#555555] text-sm">{region}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
